import { Banner, Button, Checkbox, Dialog, Radio, Select, TextInput, TextLink } from '@neo4j-ndl/react';
import { AURA_CONSOLE_EVENTS, OPS_EVENTS } from '@nx/analytics-service';
import { APP_SCOPE } from '@nx/constants';
import { createLogger } from '@nx/logger/legacy';
import type { Instance, Project } from '@nx/state';
import { NEO4J_MANAGED_KEY, TIER, consoleApi, getApiError, getErrorMessage, trackEvent } from '@nx/state';
import { isNotNullish, isNullish } from '@nx/stdlib';
import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import React, { useEffect, useState } from 'react';
import type { SingleValue } from 'react-select';

import {
  filterAvailableCloneInstances,
  validate as validateCloneToExisting,
} from '../../../../../upx/src/instance/clone/to-existing/entities/helper';
import { getTargetInstanceOptions } from '../../../../../upx/src/instance/clone/to-existing/form';
import { track } from '../../../services/segment/analytics';
import { kindOfMigrationFromBool, testMigrationPrefix } from './common';

interface Props {
  onAccept: (props: NewInstanceProps | null) => void;
  onDecline: () => void;
  liveMigration: boolean;
  name: string;
  project: Project;
  instance: Instance;
}

export interface NewInstanceProps {
  id: string;
  password: string;
  boltUrl: string;
  name: string;
  username: string;
  databaseName?: string;
}

export const ConfigureTargetMigrationDialogStep = ({
  onAccept,
  onDecline,
  liveMigration,
  name,
  project,
  instance,
}: Props) => {
  const [newInstance, setNewInstance] = useState(true);
  const [getInstance] = consoleApi.useLazyGetInstanceQuery();
  const [targetInstances, setTargetInstances] = useState<Instance[]>([]);
  const [targetInstance, setTargetInstance] = useState<Instance>();
  const { data: instanceSummaries = [] } = consoleApi.useListInstancesQuery(project.id);
  const [confirmed, setConfirmed] = useState(false);
  const [getNewStoreFormat, setGetNewStoreFormat] = useState(true);
  const [alreadyClicked, setAlreadyClicked] = useState(false);
  const [createInstance] = consoleApi.useCreateInstanceMutation();
  const [overwriteInstance] = consoleApi.useOverwriteInstanceMutation();
  const logger = createLogger(APP_SCOPE.framework);
  const { tierConfigs } = project;
  const defaultTierConfig = tierConfigs[instance.tier];
  const config = tierConfigs[instance.tier];
  const size = config?.sizes.find((s) => s.memory === instance.desiredSettings.memory);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [updateInstanceName] = consoleApi.useUpdateInstanceNameMutation();

  const handleRadioToggle = () => {
    setNewInstance(!newInstance);
    setTargetInstance(undefined);
    setConfirmed(false);
    setGetNewStoreFormat(true);
    setErrorMessage(null);
  };

  function prepareTargetOptions() {
    const allOptions = getTargetInstanceOptions(instance, targetInstances)
      .filter((option) => option.value.desiredSettings.version !== '4')
      .sort((a, b) => a.label.localeCompare(b.label));
    const disabled = allOptions.filter((option) => option.isDisabled);
    const actualOptions = allOptions.filter((option) => !option.isDisabled);
    return actualOptions.concat(disabled);
  }

  const options = prepareTargetOptions();

  const handleConfirmedChange = (checked: boolean) => {
    setConfirmed(checked);
  };

  const handleStayOnOldStoreFormat = (checked: boolean) => {
    setGetNewStoreFormat(checked);
  };

  const handleInstanceChange = (
    option: SingleValue<{
      label: string;
      value: Instance;
      key: string;
      isDisabled: boolean;
      description: string;
    }>,
  ) => {
    if (isNotNullish(option)) {
      setTargetInstance(option.value);
    }
  };

  if (isNullish(defaultTierConfig)) {
    logger.error(`TierConfig for ${instance.tier} is missing.`);
    throw new Error(`TierConfig for ${instance.tier} is missing.`);
  }

  if (isNullish(size)) {
    logger.error('size is nullish');
    throw new Error(`size is nullish`);
  }

  function newInstanceName(n: string, i: number): string {
    return liveMigration ? n : `${testMigrationPrefix}${n.substring(0, 16)}(${i})`.trim();
  }

  const nextTestNumber = (): number => {
    let n = 1;
    instanceSummaries.forEach((inst) => {
      if (inst.name.startsWith(testMigrationPrefix)) {
        const startChar = inst.name.search('\\((\\d+)\\)$');
        if (startChar !== -1) {
          const candidate = Number(inst.name.slice(startChar + 1, inst.name.length - 1));
          if (candidate >= n) {
            n = candidate + 1;
          }
        }
      }
    });
    return n;
  };

  const newName = newInstanceName(name, liveMigration ? -1 : nextTestNumber());

  const handleCreateNew = () => {
    const payloadData = {
      tier: instance.tier,
      region: instance.region,
      version: '5',
      cloudProvider: instance.cloudProvider,
      name: newName,
      memory: size.memory,
      projectId: project.id,
      storage: size.storage,
      organizationId: project.organizationId,
      sourceSnapshot: {
        dbId: instance.id,
      },
      ...(instance.tier === TIER.ENTERPRISE && {
        encryptionKeyRef: instance.encryptionKeyRef === NEO4J_MANAGED_KEY ? undefined : instance.encryptionKeyRef,
      }),
      isTrial: size.isTrial,
      // always false if flag isn't set
      migrateToBlockFormat: getNewStoreFormat,
    };

    createInstance(payloadData)
      .unwrap()
      .then((result) => {
        onAccept(result);
        logger.info(result);
      })
      .catch((e: FetchBaseQueryError | SerializedError | undefined) => {
        const error = getApiError(e);
        if (isNotNullish(error.message)) {
          setErrorMessage(error.message);
          logger.error(error.message, error.reason, error.extra, error.validationErrors);
        }
      });
    trackEvent({
      event: AURA_CONSOLE_EVENTS.INSTANCE_CLONE_TO_NEW,
      properties: {
        sourceTier: instance.tier,
        targetTier: instance.tier,
      },
      scope: APP_SCOPE.aura,
    });
  };

  const handleCloneToExisting = () => {
    if (isNullish(targetInstance)) {
      logger.error('target instance does not exist');
      return;
    }

    const dataForExisting = {
      name: newName,
      confirmed: confirmed,
      targetInstance: targetInstance,
    };

    const error = validateCloneToExisting(dataForExisting);
    if (isNotNullish(error)) {
      logger.error(error);
      return;
    }
    if (isNullish(dataForExisting.targetInstance)) {
      logger.error('target instance does not exist');
      return;
    }

    function cloneToExisting() {
      overwriteInstance({
        sourceDbId: instance.id,
        dbId: dataForExisting.targetInstance.id,
        migrateToBlockFormat: getNewStoreFormat,
      })
        .unwrap()
        .then(() => onAccept(null))
        .catch((err: FetchBaseQueryError | SerializedError | undefined) => {
          const apiError = getApiError(err);
          const message = getErrorMessage(apiError);
          setErrorMessage(message);
          logger.error(message, apiError.reason, apiError.extra, apiError.validationErrors);
        });
      trackEvent({
        event: AURA_CONSOLE_EVENTS.INSTANCE_CLONE_TO_EXISTING,
        properties: {
          sourceTier: instance.tier,
          targetTier: dataForExisting.targetInstance.tier,
        },
        scope: APP_SCOPE.aura,
      });
    }

    const shouldUpdateInstanceName = !liveMigration && !targetInstance.name.startsWith(testMigrationPrefix);

    if (shouldUpdateInstanceName) {
      updateInstanceName({ dbId: dataForExisting.targetInstance.id, name: dataForExisting.name })
        .unwrap()
        .then(() => cloneToExisting())
        .catch((e: FetchBaseQueryError | SerializedError | undefined) => {
          const apiError = getApiError(e);
          const message = getErrorMessage(apiError);
          setErrorMessage(message);
          logger.error(message, apiError.reason, apiError.extra, apiError.validationErrors);
        });
    } else {
      cloneToExisting();
    }
  };

  const handleNext = () => {
    // disable the button
    setAlreadyClicked(true);

    if (newInstance) {
      if (liveMigration) {
        track(OPS_EVENTS.MIGRATION_READINESS_REPORT_CREATE_LIVE_INSTANCE, { kind: 'clone-to-new' });
      } else {
        track(OPS_EVENTS.MIGRATION_READINESS_REPORT_CREATE_TEST_INSTANCE, { kind: 'clone-to-new' });
      }
      handleCreateNew();
    } else {
      if (liveMigration) {
        track(OPS_EVENTS.MIGRATION_READINESS_REPORT_CREATE_LIVE_INSTANCE, { kind: 'clone-to-existing' });
      } else {
        track(OPS_EVENTS.MIGRATION_READINESS_REPORT_CREATE_TEST_INSTANCE, { kind: 'clone-to-existing' });
      }
      handleCloneToExisting();
    }
  };

  const canEnableNextButton = (): boolean => {
    if (alreadyClicked) {
      return false;
    }
    if (!confirmed) {
      return false;
    }
    if (!newInstance) {
      return !isNullish(targetInstance);
    }
    return true;
  };

  useEffect(() => {
    Promise.all(instanceSummaries.map((i) => getInstance(i.id).unwrap()))
      .then((list) => setTargetInstances(filterAvailableCloneInstances(instance, list)))
      .catch((err: FetchBaseQueryError | SerializedError | undefined) => {
        const apiError = getApiError(err);
        const message = getErrorMessage(apiError);
        setErrorMessage(message);
        logger.error(message, apiError.reason, apiError.extra, apiError.validationErrors);
      });
  }, [getInstance, instance, instanceSummaries, logger]);

  const description = (): string => {
    if (liveMigration) {
      return 'This will be the destination that your Neo4j version 4 data will migrate to.';
    }
    return 'This will be the destination that your Neo4j version 4 data will migrate to for testing.';
  };

  const overwrittenCheckLabel = `I understand the target instance will be overwritten. The new name of the instance will be: ${newName}`;
  return (
    <>
      <Dialog.Header>Configure target instance for {kindOfMigrationFromBool(liveMigration)} migration</Dialog.Header>
      <Dialog.Description>{description()}</Dialog.Description>
      <Dialog.Content>
        <div className="justified mb-4 flex gap-6">
          <Radio
            label="Clone to new instance (Recommended)"
            isChecked={newInstance}
            onChange={handleRadioToggle}
            htmlAttributes={{
              'data-testid': 'target-new-instance-radio',
            }}
          />
          <Radio
            label="Clone to an existing instance"
            isChecked={!newInstance}
            onChange={handleRadioToggle}
            htmlAttributes={{
              'data-testid': 'target-existing-instance-radio',
            }}
          />
        </div>
        {/* Clone to new instance*/}
        {newInstance && (
          <div className="border-neutral-border-weak rounded-[8px] border px-3 py-3">
            <div className="mb-4 flex">
              <TextInput className="width=auto flex-1" label="Name" value={newName} isReadOnly />
              <TextInput className="flex-1" label="Version" value={'Latest'} isReadOnly />
            </div>
            <div className="ndl-divider ndl-divider-horizontal" role="separator" aria-orientation="horizontal">
              <div className="justified mb-4 mt-4 flex">
                <TextInput className="width=auto flex-1" label="Type" value={instance.tierDisplayName} isReadOnly />
                <span className="flex-1">
                  <span className="flex">
                    <TextInput label="RAM" value={size.memory} isReadOnly />
                    <TextInput label="Storage" value={size.storage} isReadOnly />
                    <TextInput label="CPU" value={size.cpu} isReadOnly />
                  </span>
                </span>
              </div>
            </div>
            <div className="ndl-divider ndl-divider-horizontal" role="separator" aria-orientation="horizontal">
              <div className="justified mb-4 mt-4 flex gap-8">
                <Checkbox
                  label={`Migrate database store format to block storage (recommended)`}
                  onChange={(event) => handleStayOnOldStoreFormat(event.target.checked)}
                  isDisabled={false}
                  isChecked={getNewStoreFormat}
                />
                <TextLink
                  type="external"
                  href="https://neo4j.com/docs/operations-manual/current/database-internals/store-formats/"
                >
                  Docs on store formats
                </TextLink>
              </div>
            </div>
            <div className="ndl-divider ndl-divider-horizontal" role="separator" aria-orientation="horizontal">
              <div className="justified mt-4 flex gap-8">
                <Checkbox
                  label={`I understand that this will create running cost for the new ${kindOfMigrationFromBool(liveMigration)} migration clone instance. I have reviewed the pricing details in my contract.`}
                  onChange={(event) => handleConfirmedChange(event.target.checked)}
                  isDisabled={false}
                  isChecked={confirmed}
                />
              </div>
            </div>
          </div>
        )}
        {/* Clone to existing instance*/}
        {!newInstance && (
          <div>
            <Banner
              type="info"
              description="Cloning into an existing instance will overwrite all of its existing data and name. This action cannot be undone and may take longer than cloning to a new instance. If you are unsure, please take a snapshot and download it before continuing."
              hasIcon
              usage="inline"
            />
            <div className="border-neutral-border-weak mt-4 justify-between rounded-[8px] border px-3 py-3">
              <div className="justified mb-4 flex">
                <div className="justify display-inline flex-1">
                  <Select
                    className="mr-8"
                    type="select"
                    label={'Destination'}
                    selectProps={{
                      placeholder: 'Select instance',
                      isDisabled: false,
                      isSearchable: false,
                      options: options,
                      isMulti: false,
                      onChange: handleInstanceChange,
                    }}
                  />
                </div>
                <div className="justify display-inline flex-1">
                  <TextInput label="Version" value={'Latest'} isReadOnly isFluid />
                </div>
              </div>
              <div className="ndl-divider ndl-divider-horizontal" role="separator" aria-orientation="horizontal">
                <div className="justified mb-4 mt-4 flex">
                  <div className="justify display-inline width=auto flex-1">
                    <TextInput
                      label="Type"
                      value={isNullish(targetInstance) ? '' : targetInstance.tierDisplayName}
                      isReadOnly
                    />
                  </div>
                  <span className="flex-1">
                    <span className="flex">
                      <div className="justify display-inline">
                        <TextInput
                          label="RAM"
                          value={isNullish(targetInstance) ? '' : targetInstance.desiredSettings.memory}
                          isReadOnly
                        />
                      </div>
                      <div className="justify display-inline">
                        <TextInput
                          label="Storage"
                          value={isNullish(targetInstance) ? '' : targetInstance.desiredSettings.storage}
                          isReadOnly
                        />
                      </div>
                      <div className="justify display-inline">
                        <TextInput
                          label="CPU"
                          value={isNullish(targetInstance) ? '' : targetInstance.desiredSettings.cpu}
                          isReadOnly
                        />
                      </div>
                    </span>
                  </span>
                </div>
              </div>
              <div className="ndl-divider ndl-divider-horizontal" role="separator" aria-orientation="horizontal">
                <div className="justified mt-4 flex gap-8">
                  <Checkbox
                    label={`Migrate database store format to block storage (recommended)`}
                    onChange={(event) => handleStayOnOldStoreFormat(event.target.checked)}
                    isDisabled={false}
                    isChecked={getNewStoreFormat}
                  />
                  <TextLink
                    type="external"
                    href="https://neo4j.com/docs/operations-manual/current/database-internals/store-formats/"
                  >
                    Docs on store formats
                  </TextLink>
                </div>
              </div>
            </div>
            <div className="ndl-divider ndl-divider-horizontal" role="separator" aria-orientation="horizontal">
              <div className="justified mt-4 flex gap-8">
                <Checkbox
                  label={overwrittenCheckLabel}
                  onChange={(event) => handleConfirmedChange(event.target.checked)}
                  isDisabled={false}
                  isChecked={confirmed}
                />
              </div>
            </div>
          </div>
        )}
        {isNotNullish(errorMessage) && (
          <Banner description={errorMessage} type="danger" className="mt-4" hasIcon usage="inline" />
        )}
      </Dialog.Content>
      <Dialog.Actions>
        <Button onClick={onDecline} color="neutral" fill="outlined" size="medium" isDisabled={alreadyClicked}>
          Cancel
        </Button>
        <Button
          onClick={() => handleNext()}
          color="primary"
          fill="filled"
          size="medium"
          isDisabled={!canEnableNextButton()}
        >
          Run {kindOfMigrationFromBool(liveMigration)} migration
        </Button>
      </Dialog.Actions>
    </>
  );
};
